import { useQuery } from 'react-query';
import { Loader, ManyImagesUpload, PageTitleWithBack } from '../../../components';
import {
  getAuthorityOptions,
  getBulkEditJobs,
  getBulkNextLotNumbers,
  getClearingLevels,
  getNeighborhoodSeriesColorExclusionOptions,
  getCooktopOptions,
  getDrainageOptions,
  getDrivewaySides,
  getEnvironmentalConcerns,
  getFirePlaceOptions,
  getFoundationTypes,
  getGarageOptions,
  getJobStatuses,
  getJobTypes,
  getPlantPackages,
  getSepticOptions,
  getSewerPumpTypes,
  getSitePrepBudgetLevels,
  getSoilConditions,
  getSurveyRequirements,
  getWaterHeaterOptions,
  updateBulkEditJobs,
  getStates,
  getJobNeighborhoodSeriesOptions
} from '../../../fetch';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import {
  IAuthorities,
  IBulkEditJob,
  IColorExclusions,
  IDropdownResponse,
  IState,
  IStateRes,
  IUpdateBulkEditJobs
} from '../../../models';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Grid,
  Stack,
  TableContainer,
  TableRow,
  TableHead,
  TableBody,
  Paper,
  TableCell,
  Table,
  FormControl,
  Typography,
  IconButton
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useSnackbar } from 'notistack';
import { Fragment, useMemo, useState } from 'react';
import jsonpatch from 'fast-json-patch';
import { Add, Close } from '@mui/icons-material';
import { convertToNumber } from '../../../helpers';

interface IEditOption {
  description: string;
  displayType: string;
  name: string;
  type?: string;
}

const editOptions: IEditOption[] = [
  { description: 'Series', displayType: 'select', name: 'neighborhoodSeriesId', type: 'number' },
  { description: 'Status', displayType: 'select', name: 'status' },
  { description: 'Type', displayType: 'select', name: 'jobType' },
  { description: 'Address', displayType: 'textfield', name: 'address', type: 'text' },
  { description: 'City', displayType: 'textfield', name: 'city', type: 'text' },
  { description: 'State', displayType: 'select', name: 'state', type: 'text' },
  { description: 'ZIP', displayType: 'textfield', name: 'zip', type: 'number' },
  {
    description: 'Garage Options',
    displayType: 'select',
    name: 'availableGarageOptions',
    type: 'multiple'
  },
  { description: 'Driveway Side', displayType: 'select', name: 'drivewaySide' },
  {
    description: 'Sales Swap Driveway Side',
    displayType: 'booleanSelect',
    name: 'canSalesSwapDrivewaySide'
  },
  { description: 'Soil Conditions', displayType: 'select', name: 'soilCondition' },
  {
    description: 'Depth Of Undercut',
    displayType: 'textfield',
    name: 'depthOfUndercut',
    type: 'number'
  },
  {
    description: 'Has Post Tensioned Slab',
    displayType: 'booleanSelect',
    name: 'hasPostTensionedSlab'
  },
  {
    description: 'Survey Requirements',
    displayType: 'select',
    name: 'surveyRequirements',
    type: 'multiple'
  },
  { description: 'Lot Fenced Standard', displayType: 'booleanSelect', name: 'lotFencedStandard' },
  {
    description: 'Environmental Concerns',
    displayType: 'select',
    name: 'environmentalConcerns',
    type: 'multiple'
  },
  { description: 'Has Minimum FFE', displayType: 'booleanSelect', name: 'hasMinimumFFE' },
  { description: 'Minimum FFE', displayType: 'textfield', name: 'minimumFFE', type: 'number' },
  { description: 'Has Sewer Pump', displayType: 'booleanSelect', name: 'hasSewerPump' },
  { description: 'Sewer Pump Type', displayType: 'select', name: 'sewerPumpType' },
  { description: 'Notes', displayType: 'textfield', name: 'notes' },
  {
    description: 'Excluded Color Schemes',
    displayType: 'select',
    name: 'excludedColorSchemes',
    type: 'multiple'
  },
  { description: 'Foundation Type', displayType: 'select', name: 'foundationType' },
  {
    description: 'Foundation Height',
    displayType: 'textfield',
    name: 'foundationHeight',
    type: 'number'
  },
  { description: 'Site Prep Budget', displayType: 'select', name: 'sitePrepBudget' },
  { description: 'Setback Front', displayType: 'textfield', name: 'setbackFront', type: 'number' },
  {
    description: 'Setback Short Side',
    displayType: 'textfield',
    name: 'setbackShortSide',
    type: 'number'
  },
  {
    description: 'Setback Long Side',
    displayType: 'textfield',
    name: 'setbackLongSide',
    type: 'number'
  },
  {
    description: 'Setback Street Side',
    displayType: 'textfield',
    name: 'setbackYardStreetSide',
    type: 'number'
  },
  { description: 'Setback Rear', displayType: 'textfield', name: 'setbackRear', type: 'number' },
  {
    description: 'Needs Foundation Inspection',
    displayType: 'booleanSelect',
    name: 'needsFoundationInspection'
  },
  {
    description: 'Possible Fit Issues',
    displayType: 'booleanSelect',
    name: 'possibleLotFitIssues'
  },
  { description: 'Lot Fit Notes', displayType: 'textfield', name: 'lotFitNotes' },
  {
    description: 'Landscaping Budget Amount',
    displayType: 'textfield',
    name: 'landscapingBudgetAmount',
    type: 'number'
  },
  { description: 'Clearing Required', displayType: 'booleanSelect', name: 'isClearingRequired' },
  { description: 'Clearing Level', displayType: 'select', name: 'clearingLevel' },
  {
    description: 'Clearing Width Driveway Side',
    displayType: 'textfield',
    name: 'clearingWidthDrivewaySide',
    type: 'number'
  },
  {
    description: 'Clearing Width Non Driveway Side',
    displayType: 'textfield',
    name: 'clearingWidthNonDrivewaySide',
    type: 'number'
  },
  {
    description: 'Clearing Depth Behind House',
    displayType: 'textfield',
    name: 'clearingDepthBehindHouse',
    type: 'number'
  },
  { description: 'Base Plant Package', displayType: 'select', name: 'basePlantPackage' },
  {
    description: 'Excluded Plant Packages',
    displayType: 'select',
    name: 'excludedPlantPackages',
    type: 'multiple'
  },
  { description: 'Island In Yard', displayType: 'booleanSelect', name: 'islandInYard' },
  { description: 'Tree In Yard', displayType: 'booleanSelect', name: 'treeInYard' },
  { description: 'Lot Cost', displayType: 'textfield', name: 'lotCost', type: 'number' },
  { description: 'Lot Premium', displayType: 'textfield', name: 'lotPremium', type: 'number' },
  { description: 'Basis Adder', displayType: 'textfield', name: 'basisAdder', type: 'number' },
  { description: 'Construction Lender', displayType: 'textfield', name: 'constructionLender' },
  {
    description: 'Construction Loan Amount',
    displayType: 'textfield',
    name: 'constructionLoanAmount',
    type: 'number'
  },
  {
    description: 'Monthly Interest Rate',
    displayType: 'textfield',
    name: 'monthlyInterestRate',
    type: 'number'
  },
  {
    description: 'Monthly Accrual Rate',
    displayType: 'textfield',
    name: 'monthlyAccrualRate',
    type: 'number'
  },
  { description: 'Power Authority', displayType: 'select', name: 'powerAuthorityId' },
  { description: 'Water Authority', displayType: 'select', name: 'waterAuthorityId' },
  { description: 'Drainage Type', displayType: 'select', name: 'drainageType' },
  { description: 'Sewer Authority', displayType: 'select', name: 'sewerAuthorityId' },
  { description: 'Septic Type', displayType: 'select', name: 'septicType' },
  { description: 'Has Gas', displayType: 'booleanSelect', name: 'hasGas' },
  { description: 'Gas Authority ID', displayType: 'select', name: 'gasAuthorityId' },
  { description: 'Water Heater Type', displayType: 'select', name: 'waterHeaterType' },
  { description: 'Fire Place Type', displayType: 'select', name: 'firePlaceType' },
  { description: 'Cook Top Type', displayType: 'select', name: 'cookTopType' },
  { description: 'Marketable', displayType: 'booleanSelect', name: 'lotIsMarketable' },
  { description: 'Platted', displayType: 'booleanSelect', name: 'hasBeenPlatted' },
  { description: 'Form Date', displayType: 'date', name: 'formDate' },
  { description: 'Start Week', displayType: 'textfield', name: 'startWeek', type: 'number' },
  { description: 'Frame Date', displayType: 'date', name: 'startDate' },
  {
    description: 'Estimated Completion Date',
    displayType: 'date',
    name: 'estimatedCompletionDate'
  },
  { description: 'Estimated Closing Date', displayType: 'date', name: 'esimatedClosingDate' },
  { description: 'Actual Closing Date', displayType: 'date', name: 'actualClosingDate' },
  { description: 'Walkthrough Date', displayType: 'date', name: 'walkthroughDate' },
  { description: 'Rewalk Date', displayType: 'date', name: 'rewalkDate' },
  {
    description: 'Compaction Test Expiration',
    displayType: 'date',
    name: 'compactionTestExpiration'
  },
  { description: 'Compaction Test File', displayType: 'file', name: 'compactionTestLetterOne' }
];

export const JobBulkEditDetails = () => {
  const [isSaving, setIsSaving] = useState(false);
  const locationState = useLocation()?.state as { jobIds: string[] };
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [numberOfRows, setNumberOfRows] = useState<string>('');
  const [compactionLetters, setCompactionLetters] = useState<{ [id: string | number]: File }>(null);
  const [jobsBulkData, setJobsBulkData] = useState<IBulkEditJob[]>();
  const [inputValue, setInputValue] = useState<string>('');
  const [selectedEditOptions, setSelectedEditOptions] = useState([]);

  const flattenEditOptions = useMemo(() => {
    return selectedEditOptions.map(option => option.description);
  }, [selectedEditOptions]);

  const { isLoading } = useQuery<IBulkEditJob[], Error>(
    ['getBulkEditJobs', locationState?.jobIds],
    () =>
      getBulkEditJobs({
        jobIds: locationState?.jobIds,
        sortBy: 'neighborhoodName',
        sortDirection: 'Asc'
      }),
    {
      enabled: !!locationState?.jobIds,
      onError: () => {
        history.push(`/development/jobs`);
      },
      onSuccess: d => {
        setJobsBulkData(d);
      }
    }
  );

  const { data: jobStatuses } = useQuery<IDropdownResponse[], Error>(['getJobStatuses'], () =>
    getJobStatuses()
  );

  const { data: jobTypes } = useQuery<IDropdownResponse[], Error>(['getJobTypes'], () =>
    getJobTypes()
  );
  const { data: soilConditions } = useQuery<IDropdownResponse[], Error>(['getSoilConditions'], () =>
    getSoilConditions()
  );
  const { data: surveyRequirements } = useQuery<IDropdownResponse[], Error>(
    ['getSurveyRequirements'],
    () => getSurveyRequirements()
  );
  const { data: environmentalConcerns } = useQuery<IDropdownResponse[], Error>(
    ['getEnvironmentalConcerns'],
    () => getEnvironmentalConcerns()
  );
  const { data: sewerPumpTypes } = useQuery<IDropdownResponse[], Error>(['getSewerPumpTypes'], () =>
    getSewerPumpTypes()
  );
  const { data: foundationTypes } = useQuery<IDropdownResponse[], Error>(
    ['getFoundationTypes'],
    () => getFoundationTypes()
  );
  const { data: sitePrepBudgetLevels } = useQuery<IDropdownResponse[], Error>(
    ['getSitePrepBudgetLevels'],
    () => getSitePrepBudgetLevels()
  );
  const { data: clearingLevels } = useQuery<IDropdownResponse[], Error>(['getClearingLevels'], () =>
    getClearingLevels()
  );
  const { data: plantPackages } = useQuery<IDropdownResponse[], Error>(['getPlantPackages'], () =>
    getPlantPackages()
  );
  const { data: garageOptions } = useQuery<IDropdownResponse[], Error>(['getGarageOptions'], () =>
    getGarageOptions()
  );
  const { data: cooktopOptions } = useQuery<IDropdownResponse[], Error>(['getCooktopOptions'], () =>
    getCooktopOptions()
  );
  const { data: firePlaceOptions } = useQuery<IDropdownResponse[], Error>(
    ['getFirePlaceOptions'],
    () => getFirePlaceOptions()
  );
  const { data: waterHeaterOptions } = useQuery<IDropdownResponse[], Error>(
    ['getWaterHeaterOptions'],
    () => getWaterHeaterOptions()
  );
  const { data: drainageOptions } = useQuery<IDropdownResponse[], Error>(
    ['getDrainageOptions'],
    () => getDrainageOptions()
  );
  const { data: septicOptions } = useQuery<IDropdownResponse[], Error>(['getSepticOptions'], () =>
    getSepticOptions()
  );
  const { data: colorSchemes } = useQuery<IColorExclusions[], Error>(
    ['getNeighborhoodSeriesColorExclusionOptions'],
    () => getNeighborhoodSeriesColorExclusionOptions()
  );
  const { data: drivewaySides } = useQuery<IDropdownResponse[], Error>(['getDrivewaySides'], () =>
    getDrivewaySides()
  );
  const { data: authorityOptions } = useQuery<IAuthorities, Error>(['getAuthorityOptions'], () =>
    getAuthorityOptions()
  );

  const { data: stateOptions } = useQuery<IStateRes, Error>(['getStates'], () => getStates());

  const renderSelectOptions = (index: number, option: IEditOption) => {
    let options = [];
    if (option.description === 'Status') {
      options = jobStatuses;
    } else if (option.description === 'Type') {
      options = jobTypes;
    } else if (option.description === 'Garage Options') {
      options = garageOptions;
    } else if (option.description === 'Driveway Side') {
      options = drivewaySides;
    } else if (option.description === 'Soil Conditions') {
      options = soilConditions;
    } else if (option.description === 'Survey Requirements') {
      options = surveyRequirements;
    } else if (option.description === 'Environmental Concerns') {
      options = environmentalConcerns;
    } else if (option.description === 'Foundation Type') {
      options = foundationTypes;
    } else if (option.description === 'Site Prep Budget') {
      options = sitePrepBudgetLevels;
    } else if (option.description === 'Excluded Color Schemes') {
      options = colorSchemes;
    } else if (option.description === 'Plant Packages') {
      options = plantPackages;
    } else if (option.description === 'Cook Top Type') {
      options = cooktopOptions;
    } else if (option.description === 'Fire Place Type') {
      options = firePlaceOptions;
    } else if (option.description === 'Water Heater Type') {
      options = waterHeaterOptions;
    } else if (option.description === 'Drainage Type') {
      options = drainageOptions;
    } else if (option.description === 'Sewer Pump Type') {
      options = sewerPumpTypes;
    } else if (option.description === 'Septic Type') {
      options = septicOptions;
    } else if (option.description === 'Power Authority') {
      options = authorityOptions?.power;
    } else if (option.description === 'Water Authority') {
      options = authorityOptions?.water;
    } else if (option.description === 'Gas Authority ID') {
      options = authorityOptions?.gas;
    } else if (option.description === 'Clearing Level') {
      options = clearingLevels;
    } else if (option.description === 'Excluded Plant Packages') {
      options = plantPackages;
    } else if (option.description === 'Base Plant Package') {
      options = plantPackages;
    } else if (option.description === 'Sewer Authority') {
      options = authorityOptions?.sewer;
    } else if (option.description === 'State') {
      options = stateOptions?.records;
    }

    return (
      <FormControl fullWidth size="small">
        {option.description === 'State' ? (
          <Select
            labelId={option.description}
            name={`${index}-${option.name}`}
            defaultValue={jobsBulkData?.[index]?.data?.[option.name] ?? ''}
            size="small"
          >
            {options?.map((menuOption: IState, index) => {
              return (
                <MenuItem key={`${menuOption.stateId}-${index}`} value={menuOption.stateId}>
                  {menuOption.name}
                </MenuItem>
              );
            })}
          </Select>
        ) : option.description === 'Excluded Color Schemes' ? (
          <Select
            labelId={option.description}
            name={`${index}-${option.name}`}
            defaultValue={jobsBulkData?.[index]?.data?.[option.name] ?? ''}
            size="small"
          >
            {options?.map((menuOption: IColorExclusions, index) => {
              return (
                <MenuItem
                  key={`${menuOption.colorSchemeID}-${index}`}
                  value={menuOption.colorSchemeID}
                >
                  Color Paint Color: {menuOption.paintColor}
                </MenuItem>
              );
            })}
          </Select>
        ) : (
          <Select
            labelId={option.description}
            multiple={option?.type === 'multiple' || false}
            name={`${index}-${option.name}`}
            defaultValue={jobsBulkData?.[index]?.data?.[option.name] ?? ''}
            size="small"
          >
            {options?.map((menuOption: IDropdownResponse, index) => {
              return (
                <MenuItem key={`${menuOption.value}-${index}`} value={menuOption.value}>
                  {menuOption.description}
                </MenuItem>
              );
            })}
          </Select>
        )}
      </FormControl>
    );
  };

  if (!locationState?.jobIds) {
    return <Redirect to={`/development/jobs`} />;
  }

  return (
    <Box>
      <PageTitleWithBack
        backText="Back to Manage Jobs"
        backUrl="/development/jobs"
        title="Bulk Edit Jobs"
      />
      {isLoading && (
        <Box mt={1}>
          <Loader position="centered" type="inline" />
        </Box>
      )}
      {isSaving && <Loader position="centered" type="fullscreen" />}
      {jobsBulkData && (
        <>
          <Stack gap={0.5} mb={1}>
            <Alert severity="info" sx={{ marginBottom: '1rem' }}>
              Select as many different options as you would like to edit. You can select multiple
              options at once. When you click save you will make changes to all of the jobs
              selected.
            </Alert>
            <Autocomplete
              multiple
              id="bulk-edit-options"
              options={editOptions ?? []}
              value={selectedEditOptions}
              inputValue={inputValue}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              disableCloseOnSelect
              autoSelect={!!inputValue}
              getOptionLabel={option => option.description}
              renderOption={(props, option: IEditOption) => {
                return (
                  <li {...props} key={`${option.name}`}>
                    {option.description}
                  </li>
                );
              }}
              onChange={(event, newValue) => {
                if (newValue && newValue?.length) {
                  setSelectedEditOptions(newValue);
                } else {
                  setSelectedEditOptions([]);
                }
              }}
              renderInput={params => (
                <TextField required {...params} variant="outlined" label="Job Options" />
              )}
            />
          </Stack>
          <form
            onSubmit={async e => {
              e.preventDefault();
              e.stopPropagation();
              setIsSaving(true);
              // convert the form values to formData
              const formData = new FormData(e.target as HTMLFormElement);
              //add uploads
              if (compactionLetters) {
                for (const [key, value] of Object.entries(compactionLetters)) {
                  formData.append(key, value);
                }
              }
              // copy the original data from API
              const newEdits: IBulkEditJob[] = JSON.parse(JSON.stringify(jobsBulkData));
              // loop through the formData and update the new edits array object with its values
              formData.forEach((value, name) => {
                const [index, key] = name.split('-');
                const oldValue = newEdits[index].data[key];
                const valueType = typeof oldValue;
                // convert the value to the correct type, so the jsonpatch can compare the values
                if (valueType === 'number') {
                  newEdits[index].data[key] = Number(value);
                } else if (valueType === 'boolean' || value === 'Yes' || value === 'No') {
                  newEdits[index].data[key] = value === '' ? null : value === 'Yes';
                } else if (Array.isArray(oldValue)) {
                  newEdits[index].data[key] = value === '' ? [] : (value as string).split(',');
                } else if (value === '') {
                  newEdits[index].data[key] = null;
                } else if (key.includes('compactionTestLetterOne.fileName')) {
                  newEdits[index].data = {
                    ...newEdits[index].data,
                    compactionTestLetterOne: {
                      ...newEdits[index].data?.compactionTestLetterOne,
                      fileName: value
                    }
                  };
                } else if (key.includes('compactionTestLetterOne.base64Content')) {
                  newEdits[index].data = {
                    ...newEdits[index].data,
                    compactionTestLetterOne: {
                      ...newEdits[index].data?.compactionTestLetterOne,
                      base64Content: value
                    }
                  };
                } else {
                  newEdits[index].data[key] = value;
                }
              });
              // convert the json to an array
              const changes = JSON.parse(JSON.stringify(newEdits));
              let patch: IUpdateBulkEditJobs[] = [];
              // loop through both arrays and build out the patch object
              jobsBulkData
                .filter(changeJob => changeJob.jobId != null)
                .forEach((job, index) => {
                  let changeJob = changes.find(changeJob => changeJob.jobId === job.jobId);
                  if (changeJob) {
                    // gives back the proper patch object to send to the API
                    const diff = jsonpatch.compare(job.data, changeJob.data);
                    if (diff.length > 0) {
                      patch.push({
                        jobId: changeJob.jobId < 0 ? null : changeJob.jobId,
                        neighborhoodId: changeJob.neighborhoodId,
                        lotNumber: changeJob.lotNumber,
                        data: diff
                      });
                    }
                  }
                });

              try {
                await updateBulkEditJobs(patch);
                history.push('/development/jobs');
                enqueueSnackbar('Jobs Updated!', {
                  variant: 'success'
                });
              } catch (error: any) {
                enqueueSnackbar(error?.Detail ?? `Error saving jobs, please try again.`, {
                  variant: 'error'
                });
              } finally {
                setIsSaving(false);
              }
            }}
            noValidate
          >
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Stack gap={1}>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    <TextField
                      type="number"
                      variant="outlined"
                      autoComplete="nope"
                      value={numberOfRows}
                      onChange={e => setNumberOfRows(e.target.value)}
                      size="small"
                      sx={{ width: 75 }}
                    />
                    <Button
                      startIcon={<Add />}
                      color="primary"
                      variant="contained"
                      size="small"
                      disabled={numberOfRows === undefined || Number(numberOfRows) <= 0}
                      onClick={async () => {
                        try {
                          const lastItem = jobsBulkData[jobsBulkData.length - 1];
                          const res = await getBulkNextLotNumbers({
                            neighborhoodId: lastItem.neighborhoodId,
                            numberOfLots: convertToNumber(numberOfRows),
                            excludeLotNumbers: jobsBulkData.map(j => j.lotNumber)
                          });
                          let newData = [];
                          for (let i = 0; i < Number(numberOfRows); i++) {
                            newData.push({
                              ...lastItem,
                              jobId: -(lastItem.jobId + i + 1),
                              lotNumber: res[i],
                              data: {
                                ...lastItem.data,
                                jobForeignId: lastItem.data.jobForeignId + i + 1
                              }
                            });
                          }
                          setJobsBulkData([...jobsBulkData, ...newData]);
                          setNumberOfRows('');
                        } catch (e) {}
                      }}
                    >
                      Add Rows
                    </Button>
                  </Box>
                  <TableContainer component={Paper}>
                    <Table aria-label="job table">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{ color: theme => theme.palette.primary.main, minWidth: '5rem' }}
                          >
                            Job ID
                          </TableCell>
                          <TableCell
                            sx={{ color: theme => theme.palette.primary.main, minWidth: '10rem' }}
                          >
                            Sage ID
                          </TableCell>
                          <TableCell
                            sx={{ color: theme => theme.palette.primary.main, minWidth: '5rem' }}
                          >
                            Lot Number
                          </TableCell>
                          <TableCell
                            sx={{ color: theme => theme.palette.primary.main, minWidth: '5rem' }}
                          >
                            Neighborhood ID
                          </TableCell>
                          {editOptions.map((option, index) => {
                            return (
                              <Fragment key={`${index}`}>
                                {flattenEditOptions.includes(option.description) && (
                                  <TableCell
                                    key={option.description}
                                    sx={{
                                      minWidth: '13rem',
                                      color: theme => theme.palette.primary.main
                                    }}
                                  >
                                    {option.description}
                                  </TableCell>
                                )}
                              </Fragment>
                            );
                          })}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {jobsBulkData?.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell>{row.jobId < 0 ? '' : row.jobId}</TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                variant="outlined"
                                autoComplete="nope"
                                name={`${index}-jobForeignId`}
                                size="small"
                                label="Sage ID"
                                defaultValue={jobsBulkData[index]?.data?.jobForeignId ?? ''}
                              />
                            </TableCell>
                            <TableCell>{row.lotNumber}</TableCell>
                            <TableCell>{row.neighborhoodId}</TableCell>
                            {editOptions.map(option => {
                              return (
                                <Fragment key={option.description}>
                                  {flattenEditOptions.includes(option.description) && (
                                    <TableCell key={option.description}>
                                      {option.displayType === 'textfield' && (
                                        <TextField
                                          fullWidth
                                          variant="outlined"
                                          autoComplete="nope"
                                          name={`${index}-${option.name}`}
                                          size="small"
                                          type={option.type}
                                          defaultValue={
                                            jobsBulkData[index]?.data?.[option.name] ?? ''
                                          }
                                        />
                                      )}
                                      {/* Custom check here because series requies a dynamic API call to via a jobId so made a seperate component */}
                                      {option.displayType === 'select' &&
                                        option.description === 'Series' && (
                                          <SeriesSelect
                                            jobsBulkData={jobsBulkData}
                                            option={option}
                                            neighborhoodId={row.neighborhoodId}
                                            index={index}
                                          />
                                        )}
                                      {option.displayType === 'select' &&
                                        option.description !== 'Series' &&
                                        renderSelectOptions(index, option)}
                                      {option.displayType === 'booleanSelect' && (
                                        <BooleanSelect
                                          name={`${index}-${option.name}`}
                                          value={jobsBulkData[index]?.data?.[option.name]}
                                        />
                                      )}
                                      {option.displayType === 'date' && (
                                        <DatePicker
                                          name={`${index}-${option.name}`}
                                          defaultValue={
                                            jobsBulkData[index]?.data?.[option.name]
                                              ? new Date(jobsBulkData[index]?.data?.[option.name])
                                              : null
                                          }
                                          slotProps={{
                                            textField: {
                                              size: 'small',
                                              fullWidth: true
                                            }
                                          }}
                                        />
                                      )}
                                      {option.displayType === 'file' && (
                                        <>
                                          {compactionLetters?.[
                                            `${index}-compactionTestLetterOne.fileName`
                                          ] ? (
                                            <Typography>
                                              {
                                                compactionLetters?.[
                                                  `${index}-compactionTestLetterOne.fileName`
                                                ]
                                              }
                                              <IconButton
                                                size="small"
                                                color="inherit"
                                                onClick={() => {
                                                  setCompactionLetters({
                                                    ...compactionLetters,
                                                    [`${index}-compactionTestLetterOne.fileName`]:
                                                      null
                                                  });
                                                }}
                                              >
                                                <Close />
                                              </IconButton>
                                            </Typography>
                                          ) : (
                                            <ManyImagesUpload
                                              fileType={'.pdf'}
                                              isRequired={false}
                                              isDisabled={false}
                                              isLoading={false}
                                              imageKey={'compactionTestLetterOne.fileName'}
                                              uploadText={'Upload File'}
                                              buttonSize="small"
                                              handleFileChange={async (
                                                image: any[],
                                                files: File[]
                                              ) => {
                                                const file = files[0];
                                                const firstImage = image[0];
                                                setCompactionLetters({
                                                  ...compactionLetters,
                                                  [`${index}-compactionTestLetterOne.fileName`]:
                                                    file.name,

                                                  [`${index}-compactionTestLetterOne.base64Content`]:
                                                    firstImage.contents
                                                });
                                              }}
                                              images={[]}
                                            />
                                          )}
                                        </>
                                      )}
                                    </TableCell>
                                  )}
                                </Fragment>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" color="primary" type="submit">
                  Save
                </Button>
              </Grid>
            </Grid>
          </form>
        </>
      )}
    </Box>
  );
};

const SeriesSelect = ({ jobsBulkData, option, neighborhoodId, index }) => {
  const { data: options } = useQuery<IDropdownResponse[], Error>(
    ['getJobNeighborhoodSeriesOptions', neighborhoodId],
    () => getJobNeighborhoodSeriesOptions(undefined, neighborhoodId)
  );
  return (
    <FormControl fullWidth size="small">
      <Select
        labelId={option.description}
        multiple={option?.type === 'multiple' || false}
        name={`${index}-${option.name}`}
        defaultValue={jobsBulkData?.[index]?.data?.[option.name] ?? ''}
        size="small"
      >
        {options?.map((menuOption: IDropdownResponse, index) => {
          return (
            <MenuItem key={`${menuOption.value}-${index}`} value={menuOption.value}>
              {menuOption.description}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

const BooleanSelect = ({ name, value }) => {
  const getDefaultValue = () => {
    if (value == null) return '';
    return value ? 'Yes' : 'No';
  };
  return (
    <FormControl fullWidth size="small">
      <Select defaultValue={getDefaultValue()} name={name}>
        {['', 'Yes', 'No'].map((option, index) => {
          return (
            <MenuItem key={`${index}`} value={option} disabled={option == null}>
              {option ?? 'Select'}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};
